// ═══════════════════════════════════════════════════════════════
// Cupones del popup de suscripción — modelo (cupones-modelo.js)
//
// Los cupones se crean en el backoffice (Cupones). Cada uno vale para los
// productos que se elijan (los de Stripe: Performance, Premium y Founders
// Edition, cada uno con sus duraciones) y puede haber varios activos a la vez,
// pero nunca dos para el mismo producto. Los activos se espejan en Firestore
// config/subscriptionCoupon, que es lo que lee la app:
//
//   { coupons: [{ code, kind: 'percent'|'amount', value, currency?, products: ['premium', …] }],
//     active, code, kind, value, currency? }   ← el cupón de antes (ver toMirror)
//
// Solo informativo: el descuento lo aplica Stripe cuando el usuario teclea el
// código, así que el Promotion Code de Stripe tiene que estar limitado a los
// mismos productos (si no, el código vale también en los demás).
//
// Fuente única: lo cargan la app (antes de subscriptionCoupon.js) y el
// backoffice (backoffice/src/pages/Cupones.jsx lo importa tal cual). Sin DOM.
// Expone window.TCCouponModel.
// TikControl © 2024-2026
// ═══════════════════════════════════════════════════════════════

(function (root) {
  'use strict';

  // Las claves son el `tier` de los planes de la app (subscriptionManager.js).
  var PRODUCTS = [
    { id: 'performance', name: 'Performance' },
    { id: 'premium', name: 'Premium' },
    { id: 'founders', name: 'Founders Edition' }
  ];
  var IDS = PRODUCTS.map(function (p) { return p.id; });

  // Donde las apps anteriores a esto pintan «el cupón»: las tarjetas de
  // Performance y Premium. Solo un cupón que valga para los dos puede ir en
  // los campos sueltos del espejo sin enseñarles un precio que Stripe no cobra.
  var LEGACY_PRODUCTS = ['performance', 'premium'];

  var CODE_RE = /^[A-Za-z0-9_-]{1,30}$/;

  function productName(id) {
    for (var i = 0; i < PRODUCTS.length; i++) if (PRODUCTS[i].id === id) return PRODUCTS[i].name;
    return id;
  }

  // Sin lista (un cupón creado antes de poder elegir) = todos los productos.
  function cleanProducts(list) {
    if (!Array.isArray(list)) return IDS.slice();
    return IDS.filter(function (id) { return list.indexOf(id) !== -1; });
  }

  // Un cupón usable o null.
  function cleanCoupon(data) {
    if (!data || typeof data !== 'object') return null;
    var code = String(data.code || '').trim();
    var kind = String(data.kind || '').trim();
    var value = Number(data.value);
    if (!CODE_RE.test(code)) return null;
    if (kind !== 'percent' && kind !== 'amount') return null;
    if (!isFinite(value) || value <= 0) return null;
    if (kind === 'percent' && value > 100) return null;
    var products = cleanProducts(data.products);
    if (!products.length) return null;

    var coupon = { code: code, kind: kind, value: Math.round(value * 100) / 100 };
    if (kind === 'amount') {
      var currency = String(data.currency || '').trim().toUpperCase();
      if (currency !== 'EUR' && currency !== 'USD') return null;
      coupon.currency = currency;
    }
    coupon.products = products;
    return coupon;
  }

  // Cupones activos, limpios y sin repetir producto (si dos dicen el mismo, el primero).
  function cleanActive(list) {
    var taken = {};
    var out = [];
    (Array.isArray(list) ? list : []).forEach(function (raw) {
      var c = cleanCoupon(raw);
      if (!c) return;
      c.products = c.products.filter(function (p) { return !taken[p]; });
      if (!c.products.length) return;
      c.products.forEach(function (p) { taken[p] = true; });
      out.push(c);
    });
    return out;
  }

  // Los cupones activos que dice el espejo. Uno escrito antes de poder elegir
  // productos trae un solo cupón en los campos sueltos: vale para todos.
  function fromMirror(data) {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.coupons)) return cleanActive(data.coupons);
    return data.active === true ? cleanActive([data]) : [];
  }

  // Lo que se escribe en el espejo con los cupones activos. Los campos
  // sueltos (los que leen las apps anteriores) llevan el cupón que vale para
  // Performance y Premium; si ninguno vale para los dos, van sin cupón.
  function toMirror(list) {
    var coupons = cleanActive(list);
    var legacy = forTab(coupons, 'free');
    var out = { active: !!legacy, coupons: coupons };
    if (legacy) {
      out.code = legacy.code;
      out.kind = legacy.kind;
      out.value = legacy.value;
      if (legacy.currency) out.currency = legacy.currency;
    }
    return out;
  }

  // El cupón de un producto. En la pestaña Free de los modales (sin producto
  // propio), el que vale a la vez para Performance y Premium.
  function forTab(coupons, tab) {
    var need = tab === 'free' ? LEGACY_PRODUCTS : [tab];
    var list = Array.isArray(coupons) ? coupons : [];
    for (var i = 0; i < list.length; i++) {
      var products = list[i].products || [];
      if (need.every(function (p) { return products.indexOf(p) !== -1; })) return list[i];
    }
    return null;
  }

  // Qué productos de `products` tiene ya otro cupón activo: [{ product, code }].
  // `active` son los cupones activos (los docs, con su `products` o sin él).
  function conflicts(active, products) {
    var want = cleanProducts(products);
    var out = [];
    (Array.isArray(active) ? active : []).forEach(function (c) {
      var mine = cleanProducts(c && c.products);
      want.forEach(function (p) {
        if (mine.indexOf(p) !== -1) out.push({ product: p, code: String(c.code || '') });
      });
    });
    return out;
  }

  root.TCCouponModel = {
    PRODUCTS: PRODUCTS,
    IDS: IDS,
    LEGACY_PRODUCTS: LEGACY_PRODUCTS,
    CODE_RE: CODE_RE,
    productName: productName,
    cleanProducts: cleanProducts,
    cleanCoupon: cleanCoupon,
    fromMirror: fromMirror,
    toMirror: toMirror,
    forTab: forTab,
    conflicts: conflicts
  };
})(typeof globalThis !== 'undefined' ? globalThis : this);
